import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { useBrandingStore } from "@/store/brandingStore";
import { brandingAssetUrl } from "@/api/branding";

/**
 * F-FORCE-PWD — first-login password change.
 *
 * Cognito returns ChallengeName=NEW_PASSWORD_REQUIRED after
 * admin_create_user. The user submits a permanent password (and any
 * required attributes Cognito flagged) here; the store completes the
 * RespondToAuthChallenge round-trip and continues into the normal
 * post-login path (app-OTP if mfa_required, else dashboard).
 *
 * Same visual shell as Login / MFAVerify so the operator's mental
 * model of "I'm in the auth flow" stays consistent.
 */

const ATTRIBUTE_LABELS: Record<string, string> = {
  given_name: "First name",
  family_name: "Last name",
  name: "Full name",
  phone_number: "Phone number (E.164, e.g. +9715…)",
  email: "Email",
};

interface PasswordRule {
  label: string;
  test: (pw: string) => boolean;
}

// Cognito's default policy on this pool — keep these in sync if the
// pool's password policy is ever tightened.
const PASSWORD_RULES: PasswordRule[] = [
  { label: "At least 8 characters", test: (pw) => pw.length >= 8 },
  { label: "An uppercase letter", test: (pw) => /[A-Z]/.test(pw) },
  { label: "A lowercase letter", test: (pw) => /[a-z]/.test(pw) },
  { label: "A digit", test: (pw) => /\d/.test(pw) },
  { label: "A symbol (!@#$%…)", test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

export function SetNewPasswordPage() {
  const brand = useBrandingStore((s) => s.branding);
  const brandLogo = brandingAssetUrl(brand.logo_url);
  const navigate = useNavigate();
  const challenge = useAuthStore((s) => s.newPasswordChallenge);
  const completeChallenge = useAuthStore((s) => s.completeNewPasswordChallenge);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [attributes, setAttributes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // If the user lands here directly without an active challenge, send
  // them back to login. Same pattern as MFAVerify.
  useEffect(() => {
    if (!challenge) {
      navigate("/login", { replace: true });
    }
  }, [challenge, navigate]);

  if (!challenge) return null;

  const ruleResults = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(password) }));
  const allRulesPass = ruleResults.every((r) => r.ok);
  const passwordsMatch = password.length > 0 && password === confirm;
  const requiredAttrsFilled = challenge.requiredAttributes.every(
    (a) => (attributes[a] ?? "").trim().length > 0,
  );
  const canSubmit = allRulesPass && passwordsMatch && requiredAttrsFilled && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    // Defensive: strip any leading/trailing whitespace before submitting.
    // Cognito has been observed to accept a password with surrounding
    // whitespace in RespondToAuthChallenge but store the trimmed form,
    // so the user's "new password" silently fails on the next login.
    // Confirm-field already matched, so we trim both before sending.
    const cleanPassword = password.trim();
    if (cleanPassword !== password) {
      setError(
        "Removed surrounding whitespace from your password — please confirm and resubmit.",
      );
      setPassword(cleanPassword);
      setConfirm("");
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const result = await completeChallenge(cleanPassword, attributes);
      if ("requiresMfa" in result && result.requiresMfa) {
        navigate("/mfa");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Couldn't set your new password",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      {/* Top Header Bar — matches Login + MFAVerify */}
      <header className="flex h-16 shrink-0 items-center border-b border-white/10 bg-primary px-4 lg:h-20 lg:px-8">
        <a href="/" className="flex items-center gap-3">
          {/* `/icon.png` is the WIDE wordmark, and a fixed h-10 w-10 box
              crushed it to an illegible sliver. Constrain the HEIGHT and let
              the width follow, capped so a wide mark cannot push the row.
              Same fix as Login.tsx, which had the identical bug.

              The company name is not repeated beside it: the wordmark already
              reads it. */}
          <img
            src={brandLogo ?? "/icon.png"}
            alt={brand.company_name}
            className="h-9 w-auto max-w-[200px] object-contain object-left"
          />
        </a>
      </header>

      <div className="flex flex-1">
        {/* Left brand panel */}
        <div className="hidden bg-navy-gradient lg:flex lg:w-[60%] lg:flex-col lg:items-center lg:justify-center lg:px-12">
          <img
            src={brandLogo ?? "/logo.png"}
            alt={brand.company_name}
            className="mb-8 h-44 w-auto object-contain"
          />
          <p className="mb-10 text-center font-display text-2xl text-accent">
            Administration Portal
          </p>
          <ul className="max-w-md space-y-4 text-base text-white">
            <li className="flex items-start gap-3">
              <span className="mt-1 block h-2 w-2 flex-shrink-0 rounded-full bg-accent" />
              Set a permanent password to finish activating your account
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 block h-2 w-2 flex-shrink-0 rounded-full bg-accent" />
              The temporary password you used will be discarded
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 block h-2 w-2 flex-shrink-0 rounded-full bg-accent" />
              Pick something you can remember — no recovery email is sent
            </li>
          </ul>
        </div>

        {/* Right form panel */}
        <div className="flex flex-1 flex-col items-center justify-center bg-[#FAFAFA] px-6 py-6 lg:w-[40%]">
          <div className="w-full max-w-sm">
            <h1 className="text-[30px] font-semibold leading-tight text-gray-900">
              Set a new password
            </h1>
            <p className="mt-2 text-base text-gray-500">
              {challenge.email}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              You signed in with a temporary password. Choose a permanent
              one to continue.
            </p>

            {error && (
              <div
                className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                role="alert"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              {/* Required attributes — only renders if Cognito flagged any.
                  In our pool today the list is empty for admin-created
                  users, so this block is usually skipped. */}
              {challenge.requiredAttributes.length > 0 && (
                <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-medium text-amber-900">
                    Cognito needs these details before activating your
                    account:
                  </p>
                  {challenge.requiredAttributes.map((attr) => (
                    <div key={attr}>
                      <label
                        htmlFor={`attr-${attr}`}
                        className="block text-sm font-medium text-gray-700"
                      >
                        {ATTRIBUTE_LABELS[attr] ?? attr}
                      </label>
                      <input
                        id={`attr-${attr}`}
                        type="text"
                        value={attributes[attr] ?? ""}
                        onChange={(e) =>
                          setAttributes((prev) => ({
                            ...prev,
                            [attr]: e.target.value,
                          }))
                        }
                        className="mt-1.5 block w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* New password */}
              <div>
                <label
                  htmlFor="new-password"
                  className="block text-sm font-medium text-gray-700"
                >
                  New password
                </label>
                <div className="relative mt-1.5">
                  <input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full rounded-lg border border-neutral-300 bg-white py-3 pl-3 pr-10 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-neutral-500 hover:text-neutral-700"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {/* Confirm */}
              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={`mt-1.5 block w-full rounded-lg border bg-white py-3 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                    confirm && !passwordsMatch
                      ? "border-red-400 focus:border-red-500"
                      : "border-neutral-300 focus:border-primary"
                  }`}
                />
                {confirm && !passwordsMatch && (
                  <p className="mt-1 text-xs text-red-600">
                    Passwords don&rsquo;t match.
                  </p>
                )}
              </div>

              {/* Live rule checklist */}
              <ul className="space-y-1 text-xs">
                {ruleResults.map((r) => (
                  <li
                    key={r.label}
                    className={`flex items-center gap-2 ${
                      r.ok ? "text-emerald-700" : "text-gray-500"
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 rounded-full ${
                        r.ok ? "bg-emerald-500" : "bg-gray-300"
                      }`}
                      aria-hidden
                    />
                    {r.label}
                  </li>
                ))}
              </ul>

              <button
                type="submit"
                disabled={!canSubmit}
                className="flex h-12 w-full items-center justify-center rounded-lg bg-gold-gradient text-base font-semibold text-white shadow-sm transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {loading ? "Setting password…" : "Set password and sign in"}
              </button>
            </form>

            <p className="mt-8 text-center text-xs text-gray-400">
              &copy; {new Date().getFullYear()}{" "}
              {brand.copyright_text ?? brand.company_name}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
